/**
 * La seña: plata que deja el cliente a cuenta de la reparacion.
 *
 * Un solo pago. La caja impone la moneda, como en todo cobro. Al entregar se
 * descuenta sola.
 *
 * Con el presupuesto ya cargado, el campo arranca en el ANTICIPO que falta para
 * llegar al 60%, contando lo que el cliente ya puso. Contando y no sumando: los
 * USD 20 del diagnostico son plata suya que ya esta en este trabajo, y pedirle
 * el 60% ademas lo dejaba pagando casi todo antes de que el equipo se tocara.
 *
 * Sin presupuesto arranca en cero: ahi una seña es lo que la persona quiera
 * dejar, porque todavia no hay numero.
 */
import { useEffect, useState } from "react";

import { EditorPagos, nuevoPago } from "@/components/cobro/EditorPagos";
import {
  chequeCompleto,
  esCheque,
  type CuentaFinanciera,
  type PagoCobro,
} from "@/components/cobro/logica";
import { Importe } from "@/components/money";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { MedioPago, SenaOt } from "@/lib/tipos";

import { anticipoSugerido, leerPlata, nombreOrden, type OrdenTaller } from "./logica";

export interface DialogSenaProps {
  orden: OrdenTaller | null;
  cuentas: CuentaFinanciera[];
  cotizacion: number;
  /** Que porcentaje del presupuesto se pide para arrancar. Viene de la configuracion. */
  anticipoPct?: number;
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (pago: SenaOt) => void;
}

export function DialogSena({
  orden,
  cuentas,
  cotizacion,
  anticipoPct = 60,
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogSenaProps): JSX.Element {
  const [pagos, setPagos] = useState<PagoCobro[]>([]);

  useEffect(() => {
    if (!orden) {
      setPagos([]);
      return;
    }
    const falta = anticipoSugerido(orden, anticipoPct);
    const moneda = leerPlata(orden).moneda;
    const p = nuevoPago(cuentas, [], falta, moneda, cotizacion);
    setPagos(p ? [p] : []);
  }, [orden, cuentas, cotizacion, anticipoPct]);

  const pago = pagos[0];
  // (0155) El cheque de la seña entra a cartera: sin sus datos no se manda.
  const puede =
    pago !== undefined && pago.importe !== null && pago.importe > 0 && chequeCompleto(pago);
  const plata = orden ? leerPlata(orden) : null;

  return (
    <Dialog open={orden !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="md">
        {orden ? (
          <>
            <DialogHeader>
              <DialogTitle>Dejar una seña</DialogTitle>
              <DialogDescription>
                {nombreOrden(orden)} · {orden.equipo}. Entra a la caja ahora y se descuenta al
                entregar.
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <EditorPagos
                pagos={pagos}
                onChange={setPagos}
                cuentas={cuentas}
                objetivo={orden ? anticipoSugerido(orden, anticipoPct) : 0}
                monedaObjetivo={plata?.moneda ?? "ARS"}
                cotizacion={cotizacion}
                unoSolo
              />

              {plata && plata.precio && plata.precio > 0 ? (
                <p className="text-xs text-muted-foreground">
                  El presupuesto es{" "}
                  <Importe monto={plata.precio} moneda={plata.moneda} tamano="sm" />. Para arrancar
                  se pide el {anticipoPct}%, o sea{" "}
                  <Importe
                    monto={(plata.precio * anticipoPct) / 100}
                    moneda={plata.moneda}
                    tamano="sm"
                  />
                  {plata.senas > 0 ? (
                    <>
                      , y ya puso <Importe monto={plata.senas} moneda={plata.moneda} tamano="sm" />.
                    </>
                  ) : (
                    "."
                  )}
                </p>
              ) : plata && plata.senas > 0 ? (
                <p className="text-xs text-muted-foreground">
                  Ya dejó <Importe monto={plata.senas} moneda={plata.moneda} tamano="sm" /> antes.
                  Esta seña se suma.
                </p>
              ) : null}

              {orden.cliente_id === null || orden.cliente_id === undefined ? (
                <p className="text-xs text-muted-foreground">
                  Sin cliente cargado, la seña queda atada a la orden y a nada más. Si después se
                  asocia el cliente, no pasa a su cuenta.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Queda en la cuenta del cliente, reservada para esta reparación.
                </p>
              )}
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onCerrar}>
                Cancelar
              </Button>
              <Button
                type="button"
                cargando={guardando}
                disabled={!puede}
                onClick={() => {
                  if (!pago || pago.importe === null) return;
                  onConfirmar({
                    cuenta_id: pago.cuenta_id,
                    medio: pago.medio as MedioPago,
                    importe: pago.importe,
                    ...(esCheque(pago.medio) && pago.cheque
                      ? { cheque: { ...pago.cheque, tipo: pago.medio as "CHEQUE" | "ECHEQ" } }
                      : {}),
                  });
                }}
              >
                Registrar la seña
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
